<template>
  <div class="chart-container">
    <div class="totals">
      <div v-if="period == 'day'">
        <h2>Totals for {{ dayString }}</h2>
        <p v-if="this.dataType == 'sales'">Daily Total<CurrencyFormatter :value="dailyTotal" :currency="companySettings.currency" /></p>
        <p v-else>Daily Total {{ dailyTotal }}</p>
      </div>
      <div v-else-if="period == 'week'">
        <h2>Totals for {{ weekString }}</h2>
        <p v-if="this.dataType == 'sales'">Weekly Total <CurrencyFormatter :value="weeklyTotal" :currency="companySettings.currency" /></p>
        <p v-else>Weekly Total {{ weeklyTotal }}</p>
      </div>
      <div v-else-if="period == 'month'">
        <h2>Totals for {{ monthName }}</h2>
        <p v-if="this.dataType == 'sales'">Monthly Total <CurrencyFormatter :value="monthlyTotal" :currency="companySettings.currency" /></p>
        <p v-else>Monthly Total {{ monthlyTotal }}</p>
      </div>
      <div v-else-if="period == 'year'">
        <h2>Total for {{ yearNumber }}</h2>
        <p v-if="this.dataType == 'sales'">Yearly Total <CurrencyFormatter :value="yearlyTotal" :currency="companySettings.currency" /></p>
        <p v-else>Yearly Total {{ yearlyTotal }}</p>
      </div>
      <div v-else-if="period == 'dayRange'">
        <h2>Totals for {{ day[0] }} to {{ day[1] }}</h2>
        <p v-if="this.dataType == 'sales'">Total <CurrencyFormatter :value="dataSet1.reduce((total, data) => total + data.value, 0)" :currency="companySettings.currency" /></p>
        <p v-else>Total {{ dataSet1.reduce((total, data) => total + data.value, 0) }}</p>
      </div>

      <div v-if="this.dataType == 'sales'" class="payment-breakdown">
        <h3>Payment Breakdown</h3>
        <table class="breakdown">
          <tbody>
            <tr v-for="(payment, index) in paymentLabels" :key="index">
              <td>{{ payment }}</td>
              <td><CurrencyFormatter :value="paymentData[index].value" :currency="companySettings.currency" /></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <VChart v-if="chartType == 'line'" :option="lineChartOptions" class="chart" autoresize></VChart>
    <VChart v-if="chartType == 'bar'" :option="barChartOptions" class="chart" autoresize></VChart>
  </div>
</template>

<script>
import * as echarts from "echarts/core";
import { GridComponent, TitleComponent, LegendComponent, TooltipComponent } from "echarts/components";
import { LineChart, BarChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";
import VChart from "vue-echarts";
import { dayRange, reports } from "@monolith/helpers";
import { LoadingSpinner, CurrencyFormatter } from "@monolith/components";
import { mapGetters } from "vuex";

echarts.use([CanvasRenderer, LineChart, BarChart, GridComponent, LegendComponent, TitleComponent, TooltipComponent]);

export default {
  name: "App",
  components: {
    VChart,
    LoadingSpinner,
    CurrencyFormatter,
  },
  data() {
    return {
      labels: [],
      dataSet1: [],
      dataSet2: [],
      cats: this.period == "day" ? ["Date", "Previous Week"] : ["Sales", "Sales"],
      yName: "",
      xName: "",
      current: 0,
      previous: 0,
      company: localStorage.getItem("company"),
      dark: true,
      finalDay: [],
      darkColour: "#CB7C2D",

      dailyTotal: 0,
      weeklyTotal: 0,
      monthlyTotal: 0,
      yearlyTotal: 0,
      monthName: "",
      yearNumber: this.day[1].substring(0, 4),
      dayString: this.period == "day" ? dayRange.displayDate(this.day[1]) : "",
      weekString: dayRange.displayWeekString(this.day[1]),

      daysPayments: 0,
      paymentLabels: [],
      paymentData: [],
    };
  },
  emits: ["handleExportData"],
  props: {
    site: {
      type: String,
      required: true,
    },
    dataType: {
      type: String,
      required: true,
    },
    day: {
      type: Array,
      required: true,
    },
    otherDay: {
      type: Array,
      required: true,
    },
    period: {
      type: String,
      required: true,
    },
    chartType: {
      type: String,
      required: true,
    },
  },

  methods: {
    async exportData() {
      let dataToExport = [];
      for (let i = 0; i < this.labels.length; i++) {
        let value = this.dataSet1[i] ? this.dataSet1[i].value || this.dataSet1[i] : 0;
        dataToExport[i] = { key: this.labels[i], value: value };
      }
      this.$emit("handleExportData", { key: "totals", value: dataToExport });
      this.$emit("handleExportData", { key: "payments", value: this.paymentData });
    },

    hoursOfDay(zero = false) {
      const hoursInDay = 24;
      const hours = [];

      for (let i = 0; i < hoursInDay; i++) {
        zero ? hours.push(0) : hours.push(i);
      }
      return hours;
    },

    fillDayHours(data) {
      const hours = this.hoursOfDay(true);
      const adj = data.map((i) => ({ hour: reports.utcFix(i.key[0]), sales: i.value }));

      adj.map((row) => (hours[row.hour] = row.sales));

      let i = this.getDaySales(hours);
      this.finalDay = this.hoursOfDay().slice(i[0], i[1]);

      return hours.slice(i[0], i[1]);
    },

    getDaySales(hours) {
      const noneZero = (h) => h != 0;
      return [hours.findIndex(noneZero) - 1, hours.findLastIndex(noneZero) + 2];
    },

    groupSalesByDate(salesData, dates) {
      // if there is no sales data then return
      if (!salesData || salesData.length === 0) return [];

      let dataset = [];

      // loop through each date
      for (let i = 0; i < dates.length; i++) {
        // loop through all of the sales data
        for (let j = 0; j < salesData.length; j++) {
          // get the date from the dates and from the sales data
          const dateStr = reports.formatFromDate(dates[i]);
          const saleDateStr = reports.formatFromDate(salesData[j].key);

          // compare the two dates
          if (dateStr.toString().substring(0, 10) == saleDateStr.toString().substring(0, 10)) {
            // if matching then push the date with the vale from the sales
            dataset.push({ key: dates[i], value: salesData[j].value });
          }
        }
      }

      // create an array with the correct dates and a base value of 0
      const grouped = [
        ...Array.from({ length: dates.length }, (_, i) => ({
          key: dates[i],
          value: 0,
        })),
      ];

      // loop through the matches data in the dataset
      dataset.forEach((data) => {
        if (!data.key) return; // is this needed

        // find the index of the date that matches the data
        const index = grouped.findIndex((item) => item.key === data.key);

        // if an index is found indicating there is data on that day
        if (index !== -1) {
          // then increment the value at the index with the data value
          grouped[index].value += data.value;
        }
      });

      // return the summed data per date
      return grouped;
    },

    getWeeksWithinRange(numberOfWeeks, startDate, endDate) {
      let weekRanges = [];
      // put all the weeks into an arry
      // push start week in between start and end
      weekRanges.push(startDate);
      // loop through weeks
      for (let i = 1; i < numberOfWeeks + 1; i++) {
        let week = startDate.substring(6, 8);
        let year = startDate.substring(0, 4);
        let weekDate = year + "-W" + (parseInt(week) + i);
        weekRanges.push(weekDate);
      }
      // push last week
      weekRanges.push(endDate);

      return weekRanges;
    },

    getDatesForWeekRange(weekRanges) {
      let datesWithinWeek = [];
      // get the ranges of dates for each week

      //loop through all the weeks
      weekRanges.forEach((week) => {
        //get all the dates within the week
        let originialDates = reports.getWeekDates(week);

        //loop through all the dates and get the range of the day
        originialDates.forEach((date) => {
          let range = dayRange.getDayRange(date);

          // store the start and end of the day
          datesWithinWeek.push([range[0], range[1]]);
        });
      });

      return datesWithinWeek;
    },

    flattenDataIntoWeek(dates, data) {
      let flattenedData = [];

      // loop through all the dates
      // the index should correlate to the data index
      for (let i = 0; i < dates.length; i++) {
        const str = dates[i][1];
        const year = Number(str.slice(0, 4));

        // month index needs to be 0-11 so -1
        const month = Number(str.slice(5, 7)) - 1;
        const day = Number(str.slice(8, 10));
        const hours = Number(str.slice(11, 13));

        // create a new date object
        const date = new Date(year, month, day, hours);

        // push the new date with the data
        flattenedData.push({ key: date, value: data[i].value });
      }
      return flattenedData;
    },

    groupWeekData(data, range) {
      let groupedData = [];
      // use the dates to get their weeks
      data.forEach((date) => {
        let week = dayRange.getWeekFromDate(date.key);
        range.map((d) => {
          if (d.substring(6, 8) == week) {
            groupedData.push({ key: d, value: date.value });
          }
        });
      });

      // change the arrray from the individual dates to the week
      const result = Object.values(
        groupedData.reduce((acc, item) => {
          if (!acc[item.key]) {
            acc[item.key] = { key: item.key, value: 0 };
          }
          acc[item.key].value += item.value;
          return acc;
        }, {}),
      );

      return result;
    },

    joinMonthData(data, dates) {
      let dateAndData = [];
      for (let i = 0; i < dates.length; i++) {
        dateAndData[i] = { key: dates[i], value: data[i] };
      }

      const grouped = Object.values(
        dateAndData.reduce((acc, item) => {
          const year = item.key.getFullYear();
          const month = String(item.key.getMonth() + 1).padStart(2, "0");
          const key = `${year}-${month}`;

          if (!acc[key]) {
            acc[key] = { key: key, value: 0 };
          }
          acc[key].value += item.value;

          return acc;
        }, {}),
      );

      return grouped;
    },

    async collectData() {
      if (this.period == "day") {
        var salesData = await reports.fetchRawData(this.day[0], this.day[1], this.dataType, this.company, this.site);
        this.dataSet1 = this.fillDayHours(salesData);

        salesData = await reports.fetchRawData(this.otherDay[0], this.otherDay[1], this.dataType, this.company, this.site);
        this.dataSet2 = this.fillDayHours(salesData);

        let total = await reports.getTotals(this.day[0], this.day[1], this.dataType, this.company, this.site);
        this.dailyTotal = total[0].value;

        this.labels = this.finalDay;
      } else if (this.period == "week") {
        let week = [];
        let lastWeek = [];
        var curr_day = Date.parse(this.day[0] + ":00:00.000Z");
        var last_day = Date.parse(this.otherDay[0] + ":00:00.000Z");

        let days = reports.weekDayLabels();

        for (var day in days) {
          var dayStart = curr_day;
          var lastDayStart = last_day;

          curr_day += 1000 * 60 * 60 * 24;
          last_day += 1000 * 60 * 60 * 24;

          week.push([reports.fixDateQuery(dayStart), reports.fixDateQuery(curr_day)]);
          lastWeek.push([reports.fixDateQuery(lastDayStart), reports.fixDateQuery(last_day)]);
        }

        this.dataSet1 = await reports.fetchTimeFrameData(week, this.dataType, this.company, this.site);
        this.dataSet2 = await reports.fetchTimeFrameData(lastWeek, this.dataType, this.company, this.site);
        this.labels = reports.weekDayLabels();

        this.weeklyTotal = this.dataSet1.reduce((total, data) => {
          return total + data;
        });
      } else if (this.period == "month") {
        let month = [];
        let monthLabels = [];
        curr_day = Date.parse(this.day[0] + ":00:00.000Z");
        last_day = Date.parse(this.otherDay[0] + ":00:00.000Z");

        let numberofDays = dayRange.getNumberOfDaysInMonth(curr_day);

        for (var i = 1; i <= numberofDays; i++) {
          dayStart = curr_day;
          curr_day += 1000 * 60 * 60 * 24;

          month.push([reports.fixDateQuery(dayStart), reports.fixDateQuery(curr_day)]);
          monthLabels.push(i);
        }

        this.dataSet1 = await reports.fetchTimeFrameData(month, this.dataType, this.company, this.site);
        this.dataSet2 = [];
        this.labels = monthLabels;
        this.monthName = reports.getMonthString(month[5][0].substring(0, 7));

        this.monthlyTotal = this.dataSet1.reduce((total, data) => {
          return total + data;
        });
      } else if (this.period == "year") {
        this.$store.dispatch("loading", true);

        let monthsInYear = reports.monthLabels();
        this.labels = monthsInYear;

        let dates = reports.allDatesInRange(this.day[0], this.day[1]);
        let lastYearDates = reports.allDatesInRange(this.otherDay[0], this.otherDay[1]);

        let data1 = await reports.fetchTimeFrameData(dates[0], this.dataType, this.company, this.site);
        let data2 = await reports.fetchTimeFrameData(lastYearDates[0], this.dataType, this.company, this.site);

        let flattenedData1 = this.joinMonthData(data1, dates[1]);
        let flattenedData2 = this.joinMonthData(data2, lastYearDates[1]);

        this.dataSet1 = flattenedData1;
        this.dataSet2 = flattenedData2;

        this.yearlyTotal = this.dataSet1.reduce((t, d) => t + Number(d.value), 0);
      }
      this.$store.dispatch("loading", false);
    },

    async collectRangeData() {
      if (this.period == "dayRange") {
        // use when day range is selected
        // get the labels for the dates with in the range
        let dates = reports.getRangeDates(this.day[0], this.day[1]);
        let rangeDates = reports.allDatesInRange(this.day[0], this.day[1]); // should I use this instead?
        // use thenumbers as labels and the date object for later
        this.labels = dates[0];
        let checkingDates = dates[1];

        // there is already a day on the end so dont need to get end time just make a date object
        let startDate = dayRange.startTime(new Date(this.day[0].substring(0, 10)));
        let startDate2 = dayRange.startTime(new Date(this.otherDay[0].substring(0, 10)));
        let endDate = dayRange.endTime(dayRange.editDay(-1, new Date(this.day[1].substring(0, 10))));
        let endDate2 = dayRange.endTime(dayRange.editDay(-1, new Date(this.otherDay[1].substring(0, 10))));

        // why are these dates incorrect?
        // get the sales data from start date to end date, last year comparator
        // the dates should be T11 day before, to T11 on end day
        var salesData1 = await reports.fetchRawData(dayRange.formatDate(startDate), dayRange.formatDate(endDate), this.dataType, this.company, this.site);
        var salesData2 = await reports.fetchRawData(dayRange.formatDate(startDate2), dayRange.formatDate(endDate2), this.dataType, this.company, this.site);

        // fix the range to work for utc
        salesData1.forEach((sale) => reports.fixDateRangeUTC(sale.key[0]));
        salesData2.forEach((sale) => reports.fixDateRangeUTC(sale.key[0]));

        let theDates = [];

        // loop through all the dates and get the same date but for last year
        checkingDates.forEach((currDate) => {
          // clone before modifying
          let clone = new Date(currDate);
          clone.setFullYear(clone.getFullYear() - 1);

          theDates.push(clone);
        });

        // group the data with both sets of data and dates and assign to datasets for the graph
        this.dataSet1 = this.groupSalesByDate(salesData1, checkingDates);
        this.dataSet2 = this.groupSalesByDate(salesData2, theDates);
      } else if (this.period == "weekRange") {
        // find how many weeks are in the range
        let startDate = this.day[0];
        let endDate = this.day[1];
        let lastStartDate = this.otherDay[0];
        let numInbetweenWeeks = endDate.substring(7, 9) - startDate.substring(7, 9) - 1;

        let weekRanges = this.getWeeksWithinRange(numInbetweenWeeks, startDate, endDate);
        let lastYearWeekRanges = this.getWeeksWithinRange(numInbetweenWeeks, lastStartDate, this.otherDay[1]);

        // make the labels this years labels
        let weekLabel = [];
        weekRanges.map((week) => {
          weekLabel.push("Week - " + week.substring(6, 8));
        });
        this.labels = weekLabel;

        // store each date of the week within the range
        let datesWithinWeek = this.getDatesForWeekRange(weekRanges);
        let lastDatesWithinWeek = this.getDatesForWeekRange(lastYearWeekRanges);

        // just get the start and end dates
        let start = datesWithinWeek[0][0];
        let end = datesWithinWeek[datesWithinWeek.length - 1][1];
        let start2 = lastDatesWithinWeek[0][0];
        let end2 = lastDatesWithinWeek[lastDatesWithinWeek.length - 1][1];

        // get the data
        this.dataSet1 = await reports.fetchRawData(start, end, this.dataType, this.company, this.site);
        this.dataSet2 = await reports.fetchRawData(start2, end2, this.dataType, this.company, this.site);

        // group data by day
        let data = this.groupSalesByDate(this.dataSet1, datesWithinWeek);
        let data2 = this.groupSalesByDate(this.dataSet2, lastDatesWithinWeek);

        // flatten the data into the correct weeks from the dates
        let fullData = this.flattenDataIntoWeek(datesWithinWeek, data);
        let fullData2 = this.flattenDataIntoWeek(lastDatesWithinWeek, data2);

        // group the data set for displaying
        this.dataSet1 = this.groupWeekData(fullData, weekRanges);
        this.dataSet2 = this.groupWeekData(fullData2, lastYearWeekRanges);
      } else if (this.period == "monthRange") {
        this.$store.dispatch("loading", true);

        // get all the months within the range
        let months = reports.getMonthsWithinRange(this.day[0], this.day[1]);
        let lastYearMonths = reports.getMonthsWithinRange(this.otherDay[0], this.otherDay[1]);

        // assign the months as words to the lables
        this.labels = months[0];

        // get the months with the full date objects
        let firstDateOfMonths = months[1];
        let firstDateOfMonthsPrevious = lastYearMonths[1];

        // get every date within the months to query the database
        let allDates1 = reports.allDatesInMonth(firstDateOfMonths);
        let allDates2 = reports.allDatesInMonth(firstDateOfMonthsPrevious);

        // call the database
        this.dataSet1 = await reports.fetchTimeFrameData(allDates1[0], this.dataType, this.company, this.site);
        this.dataSet2 = await reports.fetchTimeFrameData(allDates2[0], this.dataType, this.company, this.site);

        // join all the data together in its months
        let jointData1 = this.joinMonthData(this.dataSet1, allDates1[1]);
        let jointData2 = this.joinMonthData(this.dataSet2, allDates2[1]);

        // assign the data for the graph as arrays (not objects)
        this.dataSet1 = jointData1;
        this.dataSet2 = jointData2;
      }
      this.$store.dispatch("loading", false);
    },

    async getPayments() {
      let start = "";
      let end = "";
      if (this.period == "day" || this.period == "dayRange" || this.period == "week") {
        start = this.day[0];
        end = this.day[1];
      } else if (this.period == "weekRange") {
        start = dayRange.formatDate(dayRange.startOfWeek(dayRange.getDateOfWeek(this.day[0].substring(6, 8), this.day[0].substring(0, 4))));
        end = dayRange.formatDate(dayRange.endOfWeek(dayRange.getDateOfWeek(this.day[1].substring(6, 8), this.day[1].substring(0, 4))));
      } else if (this.period == "month" || this.period == "monthRange") {
        // I DONT THINK THIS IS UTC CORRECT????
        start = dayRange.formatDate(new Date(this.day[0].substring(0, 4), this.day[0].substring(5, 7)));
        end = dayRange.formatDate(new Date(this.day[1].substring(0, 4), this.day[1].substring(5, 7)));
      }

      let payments = await reports.getPaymentData(start, end, this.company, this.site);
      let types = [];
      let labels = [];
      payments.forEach((pay) => {
        const existingType = types.find((type) => type.key === pay.key[1]);
        if (!existingType) {
          types.push({ key: pay.key[1], value: pay.value });
          labels.push(pay.key[1]);
        } else {
          existingType.value += pay.value;
        }
      });
      this.paymentLabels = labels;
      this.paymentData = types;
    },
  },

  mounted() {
    if (this.period == "day" || this.period == "week" || this.period == "month" || this.period == "year") {
      this.collectData();
      this.getPayments();
    } else {
      this.getPayments();
      this.collectRangeData();
    }
  },

  watch: {
    period: function () {
      if (this.period == "day" || this.period == "week" || this.period == "month" || this.period == "year") {
        this.collectData();
        this.getPayments();
      } else {
        this.collectRangeData();
        this.getPayments();
      }
    },
    site: function () {
      if (this.period == "day" || this.period == "week" || this.period == "month" || this.period == "year") {
        this.collectData();
        this.getPayments();
      } else {
        this.collectRangeData();
        this.getPayments();
      }
    },
    dataType: function () {
      if (this.period == "day" || this.period == "week" || this.period == "month" || this.period == "year") {
        this.collectData();
        this.getPayments();
      } else {
        this.getPayments();
        this.collectRangeData();
      }
    },
    day: function () {},
  },

  computed: {
    ...mapGetters({
      loading: "loading",
      companySettings: "companySettings",
    }),

    barChartOptions() {
      return {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "line",
          },
        },
        xAxis: {
          type: "category",
          data: this.labels,
          name: this.xName,
          nameLocation: "middle",
          nameGap: 30,
          show: true,
          nameTextStyle: {
            color: "#3bafda",
            fontSize: 16,
          },
        },
        yAxis: {
          type: "value",
          name: this.yName,
          nameLocation: "end",
          nameGap: 30,
          show: true,
          nameTextStyle: {
            color: "#3bafda",
            fontSize: 16,
            width: "100%",
          },
          axisTick: {
            alignWithLabel: true,
          },
          axisPointer: {
            link: { xAxisIndex: "axis" },
          },
        },
        series: [
          {
            data: this.dataSet1,
            type: "bar",
            name: this.cats[0],

            itemStyle: {
              color: "#3bafda",
            },
          },
          {
            data: this.dataSet2,
            type: "bar",
            name: this.cats[1],
            itemStyle: {
              color: this.darkColour,
            },
          },
        ],

        legend: {
          textStyle: {
            color: "#3bafda",
          },
          data: [this.cats[0], this.cats[1]],
        },
      };
    },
    lineChartOptions() {
      return {
        xAxis: {
          name: this.xName,
          type: "category",
          data: this.labels,
          nameLocation: "middle",
          nameGap: 30,
          nameTextStyle: {
            color: "#3bafda",
            fontSize: 16,
          },
        },
        yAxis: {
          type: "value",
          name: this.yName,
          nameLocation: "end",
          nameGap: 30,
          show: true,
          nameTextStyle: {
            color: "#3bafda",
            fontSize: 16,
            width: "100%",
          },
        },
        series: [
          {
            name: this.cats[0],
            data: this.dataSet1,
            type: "line",
            smooth: true,
            itemStyle: {
              color: "#39AAD4",
            },
            lineStyle: {
              color: "#39AAD4",
            },
            areaStyle: {
              color: "#39AAD430",
            },
          },
          {
            name: this.cats[1],
            data: this.dataSet2,
            type: "line",
            smooth: true,
            itemStyle: {
              color: this.darkColour,
            },
            lineStyle: {
              color: this.darkColour,
            },
            areaStyle: {
              color: this.darkColour,
            },
          },
        ],
        tooltip: {
          trigger: "axis",
        },
        legend: {
          textStyle: {
            color: "#3bafda",
          },
          data: [this.cats[0], this.cats[1]],
        },
      };
    },
  },
};
</script>

<style scoped lang="scss">
.chart-container {
  margin: 1rem;
  height: 60vh;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  border-radius: 10px;
  .totals {
    max-width: 50%;
    height: 50%;
    padding-left: 1rem;
    margin-top: 0;

    h3 {
      margin-left: 0;
    }
    table {
      // margin: 1rem;
      // border: 1px solid black;
      thead {
        text-transform: bold;
      }
      tbody {
        background-color: var(--primary-colour);
        td {
          padding: 0.5rem;
          border-radius: 0.25rem;
        }
      }
    }
  }
}
@media only screen and (orientation: portrait) {
  .chart-container {
    max-width: 100vw;
    padding: 2.55px;
    background-color: var(--tertiary-colour);
    flex-direction: column;
    height: 100%;
    min-height: 50%;

    .totals {
      max-width: 100vw;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }

    .chart {
      margin-top: 1rem;
      height: 100vh;
    }
  }
}
</style>
